import is from '@sindresorhus/is';
import { z } from 'zod';

import { profileTaskPhysicalityTransportSchema } from '~/shared/data-contract/profile-task';
import { skillProficiencyLevelSchema, skillTypeSchema } from '~/shared/data-contract/skill';

export const positionTranslationsSchema = z.record(z.string(), z.string());

export type PositionTranslations = z.infer<typeof positionTranslationsSchema>;

export const positionStatusSchema = z.enum(['DRAFT', 'REVIEWED', 'PUBLISHED']);

export type PositionStatus = z.infer<typeof positionStatusSchema>;

export const positionTranslationStatusSchema = z.enum(['PENDING', 'COMPLETE', 'FAILED']);

export type PositionTranslationStatus = z.infer<typeof positionTranslationStatusSchema>;

export const positionOccupancySchema = z.enum([
    'OCCUPIED',
    'APPROVED_OPEN',
    'SIGNED_HIRE_NOT_STARTED',
    'BEING_PHASED_OUT',
]);

export type PositionOccupancy = z.infer<typeof positionOccupancySchema>;

export const positionEmploymentTypeSchema = z.enum([
    'EMPLOYEE',
    'TRAINEE',
    'APPRENTICE',
    'INTERN',
    'WORKING_STUDENT',
    'CONTRACTOR',
    'TEMP',
]);

export type PositionEmploymentType = z.infer<typeof positionEmploymentTypeSchema>;

export const positionAttributeDataTypeSchema = z.enum(['OPTION', 'TEXT', 'NUMBER', 'BOOLEAN', 'DATE']);

export type PositionAttributeDataType = z.infer<typeof positionAttributeDataTypeSchema>;

export const positionOptionListSchema = z.enum(['OPEN', 'FIXED']);

export type PositionOptionList = z.infer<typeof positionOptionListSchema>;

export const invitationDeliveryStatusSchema = z.enum(['deferred', 'queued', 'sent', 'failed']);

export const invitationDeliverySummarySchema = z.object({
    deferred: z.number().int().nonnegative(),
    queued: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
export type InvitationDeliverySummary = z.infer<typeof invitationDeliverySummarySchema>;
export const releaseDeferredInvitationsSchema = z.object({
    released: z.number().int().nonnegative(),
    summary: invitationDeliverySummarySchema,
});
export type ReleaseDeferredInvitations = z.infer<typeof releaseDeferredInvitationsSchema>;
export type PositionInvitationRequest = {
    readonly emailAddress: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly method?: 'EMAIL' | 'SSO';
    readonly delivery: 'immediate' | 'deferred';
    readonly roles?: readonly string[];
};

export const positionPersonSchema = z.object({
    invitation_delivery_status: invitationDeliveryStatusSchema.nullish(),
    user_id: z.string().nullish(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string().nullish(),
});

export type PositionPerson = z.infer<typeof positionPersonSchema>;

export const positionReviewerSchema = positionPersonSchema.extend({
    review_state: z.enum(['ASSIGNED', 'APPROVED']),
});

export type PositionReviewer = z.infer<typeof positionReviewerSchema>;

export const positionCapabilitiesSchema = z.object({
    can_enter: z.boolean().nullish(),
    can_read: z.boolean().default(false),
    can_edit: z.boolean().default(false),
    can_publish: z.boolean().default(false),
    can_assign_reviewer: z.boolean().default(false),
    can_review: z.boolean().default(false),
});

export type PositionCapabilities = z.infer<typeof positionCapabilitiesSchema>;

export const positionJobArchitectureStatusSchema = z.enum(['LIVE', 'ARCHIVED', 'DELETED']);

export type PositionJobArchitectureStatus = z.infer<typeof positionJobArchitectureStatusSchema>;

export const positionAccessKindSchema = z.enum(['ALL', 'MATCH']);

export type PositionAccessKind = z.infer<typeof positionAccessKindSchema>;

export const positionAccessMatchModeSchema = z.enum(['SUBTREE', 'EQUALS']);

export type PositionAccessMatchMode = z.infer<typeof positionAccessMatchModeSchema>;

export const positionEditAreaConditionSchema = z.object({
    attribute_id: z.string(),
    match_mode: positionAccessMatchModeSchema,
    value_id: z.string(),
});

export type PositionEditAreaCondition = z.infer<typeof positionEditAreaConditionSchema>;

export const positionEditAreaSchema = z.object({
    kind: positionAccessKindSchema,
    conditions: positionEditAreaConditionSchema.array().default([]),
});

export type PositionEditArea = z.infer<typeof positionEditAreaSchema>;

export const positionArchitectureAccessSchema = z.object({
    read_global: z.boolean().default(false),
    can_read: z.boolean().default(false),
    edit_global: z.boolean().default(false),
    can_edit: z.boolean().default(false),
    edit_areas: positionEditAreaSchema
        .array()
        .nullish()
        .transform((areas) => areas ?? []),
});

export type PositionArchitectureAccess = z.infer<typeof positionArchitectureAccessSchema>;

export const positionArchitectureSchema = z.object({
    id: z.string(),
    name: z.string(),
    connected_job_architecture_id: z.string().nullish(),
    connected_job_architecture_status: positionJobArchitectureStatusSchema.nullish().catch(undefined),
    access: positionArchitectureAccessSchema.nullish(),
    is_default: z.boolean().default(false),
    managers_can_edit: z.boolean().optional(),
});

export type PositionArchitecture = z.infer<typeof positionArchitectureSchema>;

export const positionBootstrapLevelSchema = z.object({
    id: z.string(),
    architecture_id: z.string(),
    version: z.number().int().nullish(),
    titles: positionTranslationsSchema.default({}),
});

export type PositionBootstrapLevel = z.infer<typeof positionBootstrapLevelSchema>;

export const positionBootstrapSchema = z.object({
    capabilities: positionCapabilitiesSchema.default({
        can_read: false,
        can_edit: false,
        can_publish: false,
        can_assign_reviewer: false,
        can_review: false,
    }),
    architectures: positionArchitectureSchema.array().default([]),
    users: positionPersonSchema.array().default([]),
    levels: positionBootstrapLevelSchema.array().default([]),
});

export type PositionBootstrap = z.infer<typeof positionBootstrapSchema>;

export const canEnterPositions = ({ can_enter, can_read }: PositionCapabilities): boolean => can_enter ?? can_read;

export const positionArchitectureAccess = (
    { access }: PositionArchitecture,
    { can_edit, can_read }: PositionCapabilities,
): PositionArchitectureAccess =>
    access ?? { can_edit, can_read, edit_areas: [], edit_global: can_edit, read_global: can_read };

/**
 * Whether the user may read some positions: who enters Position Management, a scoped reader whose rights sit on
 * single architectures, or a transformation lead, who reads only the architectures of their assigned positions.
 */
export const canReadSomePositions = ({ architectures, capabilities }: PositionBootstrap): boolean =>
    canEnterPositions(capabilities) ||
    architectures.some((architecture) => positionArchitectureAccess(architecture, capabilities).can_read);

export const positionAttributeConstraintsSchema = z.object({
    minimum: z.number().nullish(),
    maximum: z.number().nullish(),
    max_length: z.number().int().nullish(),
    pattern: z.string().nullish(),
});

export type PositionAttributeConstraints = z.infer<typeof positionAttributeConstraintsSchema>;

export const positionAttributeSchema = z.object({
    id: z.string(),
    owner_id: z.string().nullish(),
    key: z.string(),
    label: positionTranslationsSchema.default({}),
    data_type: positionAttributeDataTypeSchema,
    is_tree_node: z.boolean().default(false),
    constraints: positionAttributeConstraintsSchema.nullish(),
    read_only: z.boolean().default(false),
    visibility_roles: z.string().array().default([]),
    archived_at: z.string().nullish(),
    option_list: positionOptionListSchema.default('OPEN'),
});

export type PositionAttribute = z.infer<typeof positionAttributeSchema>;

export const positionAttributeValueSchema = z.object({
    id: z.string(),
    architecture_id: z.string().nullish(),
    attribute_id: z.string(),
    value: z.string(),
    parent_value_id: z.string().nullish(),
    external_code: z.string().nullish(),
    sort_order: z.number().default(0),
    archived_at: z.string().nullish(),
    label: positionTranslationsSchema.default({}),
});

export type PositionAttributeValue = z.infer<typeof positionAttributeValueSchema>;

export const positionChartViewSchema = z.object({
    id: z.string(),
    architecture_id: z.string(),
    name: z.string(),
    attribute_ids: z.string().array(),
    is_default: z.boolean().default(false),
});

export type PositionChartView = z.infer<typeof positionChartViewSchema>;

export const positionWorkspaceSchema = z.object({
    revision: z.string(),
    architecture: positionArchitectureSchema,
    attributes: positionAttributeSchema.array().default([]),
    attribute_values: positionAttributeValueSchema.array().default([]),
    views: positionChartViewSchema.array().default([]),
});

export type PositionWorkspace = z.infer<typeof positionWorkspaceSchema>;

export const positionLevelDescriptorSchema = z.object({
    level_id: z.string(),
    level_version: z.number().int().nullish(),
    titles: positionTranslationsSchema.default({}),
    external_id: z.string().nullish(),
    architecture_id: z.string().nullish().catch(undefined),
    archived: z.boolean().default(false).catch(false),
});

export type PositionLevelDescriptor = z.infer<typeof positionLevelDescriptorSchema>;

export const positionAttributeValueSnapshotSchema = z.object({
    key: z.string(),
    labels: positionTranslationsSchema.default({}),
    data_type: z.string(),
    code: z.string().nullish(),
    path: z.string().array().default([]),
    value: z.string().nullish(),
    value_labels: positionTranslationsSchema.default({}),
});

export type PositionAttributeValueSnapshot = z.infer<typeof positionAttributeValueSnapshotSchema>;

export const positionAttributeAnswerSchema = z.object({
    attribute_id: z.string(),
    value_id: z.string().nullish(),
    value: z.unknown().optional(),
    path: positionAttributeValueSchema.array().default([]),
    snapshot: positionAttributeValueSnapshotSchema.nullish(),
});

export type PositionAttributeAnswer = z.infer<typeof positionAttributeAnswerSchema>;

export const positionFrameworkSourceSchema = z.object({
    framework_id: z.string(),
    revision: z.string(),
    track_id: z.string().nullish(),
    rung: z.number().int().nullish(),
    regional_band: z.object({ name: z.string(), region: z.string().nullish() }).nullish(),
});

export type PositionFrameworkSource = z.infer<typeof positionFrameworkSourceSchema>;

const positionPayTextSchema = z.object({
    source_text: z.string().nullish(),
});

export const positionPayBandSchema = z.object({
    names: positionTranslationsSchema.default({}),
    region: z.string().nullish(),
    currency: z.string().nullish(),
    pay_grade: z.string().nullish(),
    pay_type: z.enum(['SALARIED', 'HOURLY']).nullish(),
    pay_classification: z.object({ code: z.string().nullish(), source_text: z.string().nullish() }).default({}),
    salary: z
        .object({
            minimum: z.number().nullish(),
            midpoint: z.number().nullish(),
            maximum: z.number().nullish(),
        })
        .default({}),
    target_bonus: z
        .object({
            value: z.number().nullish(),
            unit: z.string().nullish(),
            source_text: z.string().nullish(),
        })
        .default({}),
    long_term_incentive: positionPayTextSchema.default({}),
    fringe_benefits: positionPayTextSchema.default({}),
});

export type PositionPayBand = z.infer<typeof positionPayBandSchema>;

export const positionPaySchema = z.object({
    source: positionFrameworkSourceSchema.nullish(),
    default_band: positionPayBandSchema.default({
        names: {},
        pay_classification: {},
        salary: {},
        target_bonus: {},
        long_term_incentive: {},
        fringe_benefits: {},
    }),
    regional_band: positionPayBandSchema.nullish(),
    applicable_band: z.enum(['DEFAULT', 'REGIONAL']).default('DEFAULT'),
});

export type PositionPay = z.infer<typeof positionPaySchema>;

export const positionStructuredSectionSchema = z.looseObject({
    id: z.string(),
    title: z.string(),
    text: z.string(),
    order: z.number().default(0),
    tags: z.string().array().default([]),
});

export type PositionStructuredSection = z.infer<typeof positionStructuredSectionSchema>;

export const positionStructuredCompetencySchema = z.looseObject({
    id: z.string(),
    title: z.string(),
    order: z.number().default(0),
});

export type PositionStructuredCompetency = z.infer<typeof positionStructuredCompetencySchema>;

export const positionStructuredTranslationSchema = z.looseObject({
    sections: positionStructuredSectionSchema.array().default([]),
    competencies: positionStructuredCompetencySchema.array().default([]),
});

export type PositionStructuredTranslation = z.infer<typeof positionStructuredTranslationSchema>;

export const positionStructuredSkillTypeSchema = z.enum(['REQUIRED', 'DEVELOPMENT', 'OPTIONAL']);

export const positionStructuredSkillSchema = z.looseObject({
    id: z.string(),
    titles: positionTranslationsSchema.default({}),
    type: positionStructuredSkillTypeSchema.default('REQUIRED'),
    proficiency: skillProficiencyLevelSchema.nullish(),
    skill_type: skillTypeSchema.nullish(),
    competency_id: z.string().nullish(),
    order: z.number().default(0),
});

export type PositionStructuredSkill = z.infer<typeof positionStructuredSkillSchema>;

export const positionStructuredTaskSchema = z.looseObject({
    id: z.string(),
    version: z.number().int(),
    order: z.number().default(0),
    titles: positionTranslationsSchema.default({}),
    translations: positionTranslationsSchema.default({}),
});

export type PositionStructuredTask = z.infer<typeof positionStructuredTaskSchema>;

export const positionTaskEditRequestSchema = z.object({
    description: z.string().max(100_000).nullish(),
    language: z.string(),
    title: z.string().trim().min(1).max(256),
    version: z.number().int().positive(),
    workflow: z
        .object({
            steps: z
                .array(
                    z.object({
                        description: z.string().trim().min(1).max(100_000),
                        physicality: profileTaskPhysicalityTransportSchema.optional(),
                        title: z.string().trim().min(1).max(256),
                    }),
                )
                .min(1)
                .max(100),
        })
        .optional(),
});

export type PositionTaskEditRequest = z.infer<typeof positionTaskEditRequestSchema>;

/**
 * A work task the Position owns, minted when a task is added or edited from it: a fork of a shared task on its first
 * edit, a new version of the fork later. It is pinned only once the Position saves a version referencing it.
 */
export const positionTaskRefSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    titles: positionTranslationsSchema.default({}),
    translations: positionTranslationsSchema.default({}),
    generation_state: z.enum(['QUEUED', 'STARTED', 'FINISHED', 'FAILED']),
});

export type PositionTaskRef = z.infer<typeof positionTaskRefSchema>;

export const positionStructuredSchema = z.looseObject({
    source_language: z.string(),
    translations: z.record(z.string(), positionStructuredTranslationSchema).default({}),
    skills: positionStructuredSkillSchema.array().default([]),
    tasks: positionStructuredTaskSchema.array().default([]),
});

export type PositionStructured = z.infer<typeof positionStructuredSchema>;

export const positionVersionSummarySchema = z.object({
    version: z.number().int(),
    status: positionStatusSchema,
    translation_status: positionTranslationStatusSchema,
    created_at: z.string(),
});

export type PositionVersionSummary = z.infer<typeof positionVersionSummarySchema>;

export const positionMetadataSchema = z
    .object({
        employment_type: positionEmploymentTypeSchema.nullish(),
        reports_to_position_id: z.string().nullish(),
        reports_to_position_ids: z.array(z.string()).optional(),
    })
    .transform(({ reports_to_position_id, reports_to_position_ids, ...metadata }) => ({
        ...metadata,
        reports_to_position_ids:
            reports_to_position_ids ?? (is.nonEmptyString(reports_to_position_id) ? [reports_to_position_id] : []),
    }));

export type PositionMetadata = z.infer<typeof positionMetadataSchema>;

export type PositionMetadataPatch = {
    readonly employment_type?: PositionEmploymentType | null;
    readonly reports_to_position_ids?: readonly string[];
};

export const positionManagerSchema = z.object({
    position_id: z.string().nullish(),
    titles: positionTranslationsSchema.default({}),
    title_source_language: z.string(),
    assignee: positionPersonSchema.pick({ first_name: true, last_name: true }).nullish(),
});

export type PositionManager = z.infer<typeof positionManagerSchema>;

export const positionDetailSchema = z.object({
    id: z.string(),
    architecture_id: z.string(),
    external_id: z.string().nullish(),
    titles: positionTranslationsSchema.default({}),
    title_source_language: z.string(),
    version: z.number().int(),
    current_version: z.number().int(),
    published_version: z.number().int().nullish(),
    reviewed_version: z.number().int().nullish(),
    versions: positionVersionSummarySchema.array().default([]),
    created_at: z.string(),
    created_by: z.string().nullish(),
    created_by_person: positionPersonSchema.nullish().catch(undefined),
    archived: z.boolean().default(false),
    status: positionStatusSchema,
    translation_status: positionTranslationStatusSchema,
    occupancy: positionOccupancySchema.nullish().catch(null),
    assignee: positionPersonSchema.nullish(),
    reviewer: positionReviewerSchema.nullish(),
    reviewed_at: z.string().nullish(),
    reviewed_by: z.string().nullish(),
    reviewed_by_person: positionPersonSchema.nullish().catch(undefined),
    published_at: z.string().nullish(),
    published_by: z.string().nullish(),
    published_by_person: positionPersonSchema.nullish().catch(undefined),
    metadata: positionMetadataSchema.prefault({}),
    managers: positionManagerSchema.array().optional().catch(undefined),
    mapped_level_item_id: z.string().nullish(),
    mapped_level: positionLevelDescriptorSchema.nullish().catch(undefined),
    based_on_level_item_id: z.string().nullish(),
    based_on_level_version: z.number().int().nullish(),
    based_on_level: positionLevelDescriptorSchema.nullish().catch(undefined),
    pay: positionPaySchema.nullish(),
    attribute_values: z.record(z.string(), positionAttributeAnswerSchema).default({}),
    structured: positionStructuredSchema.nullish(),
    can_review: z.boolean().default(false),
    can_edit: z.boolean().nullish(),
});

export type PositionDetail = z.infer<typeof positionDetailSchema>;

export const canEditPosition = (
    position: Pick<PositionDetail, 'archived' | 'can_edit'>,
    { can_edit }: Pick<PositionCapabilities, 'can_edit'>,
): boolean => position.can_edit ?? (can_edit && !position.archived);

const paginationSchema = z.object({
    pages: z.number().int(),
    count: z.number().int(),
    page: z.number().int(),
    size: z.number().int(),
});

export const positionPageSchema = z.object({
    data: positionDetailSchema.array().default([]),
    pagination: paginationSchema,
});

export type PositionPage = z.infer<typeof positionPageSchema>;

export const positionCountsNodeSchema = z.object({
    attribute_id: z.string(),
    value_id: z.string().nullable(),
    direct: z.boolean().default(false),
    label: z.string().nullable(),
    count: z.number(),
    has_children: z.boolean(),
});

export type PositionCountsNode = z.infer<typeof positionCountsNodeSchema>;

export const positionCountsResponseSchema = z.object({
    count: z.number(),
    children: positionCountsNodeSchema.array().default([]),
});

export type PositionCountsResponse = z.infer<typeof positionCountsResponseSchema>;

export const orgChartStartSchema = z.enum(['MINE', 'TOP']);

export type OrgChartStart = z.infer<typeof orgChartStartSchema>;

export const orgChartPositionSchema = z.object({
    id: z.string(),
    titles: positionTranslationsSchema.default({}),
    title_source_language: z.string(),
    holder: positionPersonSchema.pick({ first_name: true, last_name: true, user_id: true }).nullish(),
    occupancy: positionOccupancySchema.nullish().catch(null),
    attribute_line: z.string().nullish(),
});

export type OrgChartPosition = z.infer<typeof orgChartPositionSchema>;

export const orgChartNodeSchema = z.object({
    position: orgChartPositionSchema,
    direct_count: z.number().int(),
    total_count: z.number().int(),
    pending_count: z.number().int().default(0),
    matches_filter: z.boolean().default(true),
});

export type OrgChartNode = z.infer<typeof orgChartNodeSchema>;

export const orgChartLevelSchema = z.object({
    start_used: orgChartStartSchema.nullish().catch(null),
    parent: orgChartNodeSchema.nullish(),
    children: orgChartNodeSchema.array().default([]),
    pending: z.object({ position: orgChartPositionSchema }).array().default([]),
    unconnected_count: z.number().int().nullish(),
    pagination: paginationSchema,
});

export type OrgChartLevel = z.infer<typeof orgChartLevelSchema>;

export const positionEventReferencesSchema = z.object({
    people: positionPersonSchema.array().default([]),
    levels: positionLevelDescriptorSchema.array().default([]),
});

export type PositionEventReferences = z.infer<typeof positionEventReferencesSchema>;

export const positionEventSchema = z.object({
    id: z.string(),
    position_id: z.string(),
    version: z.number().int().nullish(),
    event_type: z.string(),
    actor: positionPersonSchema.nullish(),
    payload: z.unknown().optional(),
    references: positionEventReferencesSchema.nullish().catch(undefined),
    created_at: z.string(),
});

export type PositionEvent = z.infer<typeof positionEventSchema>;

export const positionEventPageSchema = z.object({
    data: positionEventSchema.array().default([]),
    pagination: paginationSchema,
});

export type PositionEventPage = z.infer<typeof positionEventPageSchema>;

export const positionReviewerCandidatesSchema = positionPersonSchema.array();

export const positionAssigneeCandidatesSchema = positionPersonSchema.array();

export const positionLevelPickerLayerSchema = z.enum(['FAMILY', 'CLUSTER', 'ROLE', 'LEVEL']);

export type PositionLevelPickerLayer = z.infer<typeof positionLevelPickerLayerSchema>;

export const positionLevelPickerNodeSchema = z.object({
    id: z.string(),
    layer: positionLevelPickerLayerSchema,
    titles: positionTranslationsSchema.default({}),
    has_children: z.boolean().default(false),
    version: z.number().int().nullish(),
});

export type PositionLevelPickerNode = z.infer<typeof positionLevelPickerNodeSchema>;

const knownPickerNodes = (candidates: readonly unknown[]): PositionLevelPickerNode[] =>
    candidates.flatMap((candidate) => {
        const node = positionLevelPickerNodeSchema.safeParse(candidate);

        return node.success ? [node.data] : [];
    });

export const positionLevelPickerChildrenSchema = z.object({
    items: z.array(z.unknown()).default([]).transform(knownPickerNodes),
    truncated: z.boolean().default(false),
});

export type PositionLevelPickerChildren = z.infer<typeof positionLevelPickerChildrenSchema>;

export const positionLevelPickerPathEntrySchema = z.object({
    id: z.string(),
    layer: z.string(),
    titles: positionTranslationsSchema.default({}),
});

export const positionLevelPickerLevelSchema = z.object({
    id: z.string(),
    architecture_id: z.string(),
    version: z.number().int(),
    titles: positionTranslationsSchema.default({}),
    path: positionLevelPickerPathEntrySchema.array().default([]),
});

export type PositionLevelPickerLevel = z.infer<typeof positionLevelPickerLevelSchema>;

export const positionLevelPickerLevelsSchema = positionLevelPickerLevelSchema.array();

const positionLevelCountSchema = z.object({
    level_id: z.string(),
    positions: z.number().int().nonnegative(),
});

export const positionLevelCountsSchema = z.object({
    counts: positionLevelCountSchema.array().default([]),
});

export type PositionLevelCounts = z.infer<typeof positionLevelCountsSchema>;

export const positionCompanyInformationSchema = z.object({
    architecture_id: z.string(),
    company_name: z.string().nullish(),
    company_domain: z.string().nullish(),
    company_description: z.string().nullish(),
    company_priorities: z.string().nullish(),
    company_size: z.number().int().nullish(),
    source_job_architecture_id: z.string().nullish(),
});

export type PositionCompanyInformation = z.infer<typeof positionCompanyInformationSchema>;

export const positionCompanyLookupStatusSchema = z.enum(['NOT_STARTED', 'PENDING', 'SUCCESS', 'FAILED']);

export const positionCompanyLookupResultSchema = z.object({
    company_description: z.string(),
    company_priorities: z.string(),
    company_size: z.number().int().nullish(),
});

export type PositionCompanyLookupResult = z.infer<typeof positionCompanyLookupResultSchema>;

export const positionCompanyLookupSchema = z.object({
    status: positionCompanyLookupStatusSchema,
    company_name: z.string().nullish(),
    company_domain: z.string().nullish(),
    updated_at: z.string().nullish(),
    result: positionCompanyLookupResultSchema.nullish(),
    error_code: z.string().nullish(),
});

export type PositionCompanyLookup = z.infer<typeof positionCompanyLookupSchema>;

export const positionPlanStatusSchema = z.enum(['QUEUED', 'GENERATING', 'APPLYING', 'APPLIED', 'FAILED', 'CANCELLED']);

export type PositionPlanStatus = z.infer<typeof positionPlanStatusSchema>;

const positionPlanRunSchema = z.object({
    stage: z.string(),
    unitsDone: z.number().int().default(0),
    unitsTotal: z.number().int().nullish(),
});

export const positionPlanErrorSchema = z.object({
    code: z.string().nullish(),
    message: z.string().nullish(),
});

export type PositionPlanError = z.infer<typeof positionPlanErrorSchema>;

export const positionPlanSchema = z.object({
    id: z.string(),
    status: positionPlanStatusSchema,
    run: positionPlanRunSchema,
    error: positionPlanErrorSchema.nullish(),
    setupRunId: z.string().nullish(),
});

export type PositionPlan = z.infer<typeof positionPlanSchema>;

export const positionPlanListItemSchema = z.object({
    id: z.string(),
    status: positionPlanStatusSchema,
    setupRunId: z.string().nullish(),
});

export type PositionPlanListItem = z.infer<typeof positionPlanListItemSchema>;

export const positionPlanListSchema = positionPlanListItemSchema.array();

export const positionPlanActiveConflictSchema = z.object({
    activePlanId: z.string().nullish(),
});

export const positionAttachmentSchema = z.object({
    id: z.string(),
    name: z.string(),
});

export type PositionAttachment = z.infer<typeof positionAttachmentSchema>;

export const positionAttachmentListSchema = z.object({
    attachments: positionAttachmentSchema.array(),
});

export type PositionAttachmentList = z.infer<typeof positionAttachmentListSchema>;

export const positionAttachmentPresignedUploadSchema = z.object({
    attachmentId: z.string(),
    fileName: z.string(),
    uploadUrl: z.string().nullable(),
});

export type PositionAttachmentPresignedUpload = z.infer<typeof positionAttachmentPresignedUploadSchema>;

export const positionAttachmentPresignResponseSchema = z.object({
    attachments: positionAttachmentPresignedUploadSchema.array(),
});

export type PositionAttachmentPresignResponse = z.infer<typeof positionAttachmentPresignResponseSchema>;

export const positionAccessGrantConditionSchema = z.object({
    attribute_id: z.string(),
    attribute_label: z.string(),
    match_mode: positionAccessMatchModeSchema,
    value_id: z.string(),
    label: z.string(),
});

export type PositionAccessGrantCondition = z.infer<typeof positionAccessGrantConditionSchema>;

export const positionAccessGrantSchema = z.object({
    id: z.string(),
    user_id: z.string(),
    user_name: z.string(),
    role: z.string(),
    architecture_id: z.string(),
    architecture_name: z.string(),
    kind: positionAccessKindSchema,
    conditions: positionAccessGrantConditionSchema.array().default([]),
    created_at: z.string(),
    created_by: z.string().nullish(),
});

export type PositionAccessGrant = z.infer<typeof positionAccessGrantSchema>;

export const positionAccessGrantListSchema = z.object({
    grants: positionAccessGrantSchema.array().default([]),
});

export type PositionAccessGrantList = z.infer<typeof positionAccessGrantListSchema>;

export const positionAccessCoverageSchema = z.object({
    covering: positionAccessGrantSchema.array().default([]),
    partial: positionAccessGrantSchema.array().default([]),
    exact_grant_ids: z.string().array().default([]),
});

export type PositionAccessCoverage = z.infer<typeof positionAccessCoverageSchema>;

export const positionAccessCandidateSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullish(),
});

export type PositionAccessCandidate = z.infer<typeof positionAccessCandidateSchema>;

export const positionAccessCandidatesSchema = z.object({
    users: positionAccessCandidateSchema.array().default([]),
});

export type PositionAccessCandidates = z.infer<typeof positionAccessCandidatesSchema>;

export const positionTransformationLeadSchema = z.object({
    position_id: z.string(),
    user_id: z.string(),
    user_name: z.string(),
    assigned_by: z.string().nullish(),
    assigned_at: z.string(),
});

export type PositionTransformationLead = z.infer<typeof positionTransformationLeadSchema>;

export const positionTransformationLeadListSchema = z.object({
    assignments: positionTransformationLeadSchema.array().default([]),
});

export type PositionTransformationLeadList = z.infer<typeof positionTransformationLeadListSchema>;

export const positionTransformationLeadCandidatesSchema = z.object({
    candidates: positionAccessCandidateSchema.array().default([]),
});

export type PositionTransformationLeadCandidates = z.infer<typeof positionTransformationLeadCandidatesSchema>;

export const managerChangeActionSchema = z.enum(['ADD', 'REMOVE']);

export type ManagerChangeAction = z.infer<typeof managerChangeActionSchema>;

export const managerChangeStatusSchema = z.enum(['QUEUED', 'APPLYING', 'DONE', 'FAILED']);

export type ManagerChangeStatus = z.infer<typeof managerChangeStatusSchema>;

export const managerChangeFailureReasonSchema = z.enum(['MANAGER_UNAVAILABLE', 'INTERRUPTED']);

export const managerChangeSkippedSchema = z.object({
    own_position: z.number(),
    loop: z.number(),
    already_set: z.number(),
    manager_limit: z.number(),
});

export type ManagerChangeSkipped = z.infer<typeof managerChangeSkippedSchema>;

export const managerChangeRunSchema = z.object({
    id: z.string(),
    architecture_id: z.string(),
    action: managerChangeActionSchema,
    manager: z.object({
        position_id: z.string(),
        title: z.string(),
        external_id: z.string().nullable(),
        holder_name: z.string().nullable(),
    }),
    publish: z.boolean(),
    status: managerChangeStatusSchema,
    failure_reason: managerChangeFailureReasonSchema.nullable().default(null),
    done: z.number(),
    total: z.number(),
    changed: z.number(),
    published: z.number(),
    skipped: managerChangeSkippedSchema,
    created_at: z.string(),
    finished_at: z.string().nullable(),
});

export type ManagerChangeRun = z.infer<typeof managerChangeRunSchema>;

export const latestManagerChangeSchema = z
    .union([managerChangeRunSchema, z.literal('')])
    .transform((run) => (run === '' ? null : run));

export const managerChangePreviewSchema = z.object({
    total: z.number(),
    changes: z.number(),
    replaced: z.number().default(0),
    skipped: managerChangeSkippedSchema,
    publish: z.object({
        now: z.number(),
        with_pending_draft: z.number(),
        never_published: z.number(),
    }),
});

export type ManagerChangePreview = z.infer<typeof managerChangePreviewSchema>;

export const managerChangeSkipReasonSchema = z.enum(['OWN_POSITION', 'LOOP', 'ALREADY_SET', 'MANAGER_LIMIT']);

export type ManagerChangeSkipReason = z.infer<typeof managerChangeSkipReasonSchema>;

export const managerChangeSkippedPositionsSchema = z.object({
    positions: z
        .object({
            position_id: z.string(),
            title: z.string(),
            external_id: z.string().nullable(),
            reason: managerChangeSkipReasonSchema,
        })
        .array()
        .default([]),
});

export type ManagerChangeSkippedPositions = z.infer<typeof managerChangeSkippedPositionsSchema>;

export const nodeManagersSchema = z.object({
    managers: z
        .object({
            position_id: z.string(),
            title: z.string(),
            external_id: z.string().nullable(),
            holder_name: z.string().nullable(),
            count: z.number(),
        })
        .array()
        .default([]),
});

export type NodeManagers = z.infer<typeof nodeManagersSchema>;

export type NodeManager = NodeManagers['managers'][number];

export type PositionAttributeFilter = {
    readonly attribute_id: string;
    readonly match: 'EXACT' | 'SUBTREE' | 'UNSET';
    readonly value_id?: string | null;
};

export type PositionFilters = {
    readonly search?: string;
    readonly attributes?: readonly PositionAttributeFilter[];
    readonly occupancy?: readonly PositionOccupancy[];
    readonly status?: readonly PositionStatus[];
    readonly reviewer?: {
        readonly match: 'ANY' | 'ASSIGNED' | 'UNASSIGNED' | 'USER';
        readonly user_id?: string;
    };
    readonly mapped_level_item_id?: string;
    readonly line_of?: string;
    readonly archived?: 'ACTIVE' | 'ARCHIVED' | 'ALL';
};

export type PositionAccessAction = 'READ' | 'EDIT';

export type PositionFilterRequest = {
    readonly action?: PositionAccessAction;
    readonly paged?: { readonly page?: number; readonly size?: number; readonly count_only?: boolean };
    readonly sorted?: {
        readonly fields: ReadonlyArray<{ readonly field: string; readonly direction: 'asc' | 'desc' }>;
    };
    readonly filters?: PositionFilters;
};

export type OrgChartRequest = {
    readonly parent_position_id: string | null;
    readonly start: OrgChartStart;
    readonly unconnected: boolean;
    readonly attribute_id: string | null;
    readonly filters: PositionFilters;
    readonly paged: { readonly count_only?: boolean; readonly page: number; readonly size: number };
};

export type PositionCountsPathEntry = {
    readonly attribute_id: string;
    readonly value_id: string | null;
    readonly direct?: boolean;
};

export type PositionCountsRequest = {
    readonly group_by: readonly string[];
    readonly filters?: PositionFilters;
    readonly parent?: readonly PositionCountsPathEntry[];
};

export type PositionAccessArea = {
    readonly kind: PositionAccessKind;
    readonly path: readonly PositionCountsPathEntry[];
};

export type PositionAccessGrantCreateRequest = PositionAccessArea & {
    readonly user_id: string;
    readonly acknowledged_grant_ids: readonly string[];
};

export type ManagerChangeRequest = {
    readonly action: ManagerChangeAction;
    readonly manager_position_id: string;
    readonly path: readonly PositionCountsPathEntry[];
};

export type ManagerChangeStartRequest = ManagerChangeRequest & { readonly publish: boolean };

export type PositionAttributeValueInput = { readonly value_id: string } | { readonly value: unknown };

export type PositionOperationsPatch = {
    readonly occupancy?: PositionOccupancy | null;
    readonly assignee?: PositionPerson | null;
    readonly external_id?: string | null;
};

export type PositionPaySelection = {
    readonly framework_id: string;
    readonly track_id: string;
    readonly rung: number;
    readonly regional_band?: { readonly name: string; readonly region?: string | null } | null;
    readonly applicable_band?: 'DEFAULT' | 'REGIONAL' | null;
};

export type PositionCreateRequest = {
    readonly architecture_id: string;
    readonly external_id?: string | null;
    readonly titles: PositionTranslations;
    readonly source_language?: string;
    readonly should_translate?: boolean;
    readonly metadata?: PositionMetadataPatch;
    readonly attribute_values?: Readonly<Record<string, PositionAttributeValueInput | null>>;
    readonly mapped_level_item_id?: string | null;
    readonly based_on_level_id?: string | null;
    readonly based_on_level_version?: number | null;
    readonly operations?: PositionOperationsPatch;
};

export type PositionPatch = {
    readonly titles?: PositionTranslations;
    readonly metadata?: PositionMetadataPatch;
    readonly attribute_values?: Readonly<Record<string, PositionAttributeValueInput | null>>;
    readonly structured?: PositionStructured | null;
    readonly copy_level_id?: string | null;
    readonly copy_level_version?: number | null;
    readonly pay?: PositionPaySelection | null;
};

export type PositionSaveRequest = {
    readonly expected_version: number;
    readonly patch: PositionPatch;
    readonly source_language?: string;
    readonly should_translate?: boolean;
    readonly operations?: PositionOperationsPatch;
};

export type PositionChartViewRequest = {
    readonly name: string;
    readonly attribute_ids: readonly string[];
    readonly is_default: boolean;
};

export type PositionAttributeCreateRequest = {
    readonly key: string;
    readonly label: PositionTranslations;
    readonly data_type: PositionAttributeDataType;
    readonly is_tree_node?: boolean;
    readonly constraints?: PositionAttributeConstraints | null;
    readonly read_only?: boolean;
    readonly visibility_roles?: readonly string[];
};

export type PositionAttributeUpdateRequest = {
    readonly label: PositionTranslations;
    readonly constraints: PositionAttributeConstraints | null;
    readonly read_only: boolean;
    readonly visibility_roles: readonly string[];
    readonly archived: boolean;
};

export type PositionAttributeValueCreateRequest = {
    readonly architecture_id: string;
    readonly attribute_id: string;
    readonly value: string;
    readonly parent_value_id?: string | null;
    readonly external_code?: string | null;
};

export type PositionAttributeValueUpdateRequest = {
    readonly value: string;
    readonly parent_value_id: string | null;
    readonly external_code: string | null;
    readonly archived: boolean;
};

export type PositionCompanyInformationUpdate = {
    readonly company_name?: string | null;
    readonly company_domain?: string | null;
    readonly company_description?: string | null;
    readonly company_priorities?: string | null;
    readonly company_size?: number | null;
};

export type PositionPlanRequest = Readonly<Record<string, unknown>>;

export type PositionPlanStartRequest = {
    readonly request: PositionPlanRequest;
    readonly setupRunId: string | null;
};

export type PositionAttachmentPresignFile =
    | { readonly contentLength: number; readonly fileName: string; readonly processWithWombat: boolean }
    | { readonly documentId: string; readonly fileName: string };
